import { Flex } from "@chakra-ui/react";
import Text from "components/base/Text";
import Trans, { Currency } from "components/base/Trans";

// Prices are configured in next.config.js in thousands of pounds. Next only
// inlines `process.env.<name>` when it is written out in full, so every
// variable is referenced literally instead of being looked up by key.
const toPounds = (thousands, fallback) => {
  const value = Number(thousands);
  return (Number.isFinite(value) && value > 0 ? value : fallback) * 1000;
};

const PRICES = {
  hx50: () => ({
    list: toPounds(process.env.hx50_base_price, 675),
    final: toPounds(process.env.hx50_campaign_price, 579),
  }),
  hc50: () => ({
    list: toPounds(process.env.hc50_base_price, 795),
    final: toPounds(process.env.hc50_campaign_price, 658),
  }),
};

// Single source for the campaign figures. The discount is always derived from
// the list and final prices so the numbers can never disagree.
export const getCampaignPrices = (model) => {
  const { list, final } = (PRICES[model] || PRICES.hx50)();
  return { list, discount: list - final, final };
};

const money = (value) => (
  <Currency value={value} minimumFractionDigits={0} maximumFractionDigits={0} />
);

/**
 * Campaign price lockup, stacked and centred:
 *   1. list price, small white ante-title with a green strikethrough
 *   2. campaign (final) price, in the same type as the surrounding spec value
 *   3. "Special Price" label, in the same type as the surrounding spec label
 *
 * The ante-title is taken out of flow on desktop so the final price keeps the
 * same baseline as every other spec value. On mobile it stays in flow.
 *
 * @param {"hx50"|"hc50"} model
 * @param {boolean} compact  smaller type and spacing for hero/spec strips
 * @param {"hero"|"spec"} variant  typography of the surrounding page
 *
 * Built from inline-safe elements (span with display) so it can sit inside a
 * paragraph, e.g. the HorizontalSpecs slot.
 */
const CampaignPriceBlock = ({
  model = "hx50",
  compact = false,
  variant = "hero",
  ...props
}) => {
  const { list, final } = getCampaignPrices(model);
  const isSpec = variant === "spec";
  const tabular = { fontVariantNumeric: "tabular-nums" };

  return (
    <Flex
      as="span"
      direction="column"
      align="center"
      position="relative"
      data-testid={`campaign-price-${model}`}
      w="100%"
      maxW={compact ? "280px" : "360px"}
      mx="auto"
      textAlign="center"
      {...props}
    >
      <Text
        as="s"
        position={["static", null, "absolute"]}
        bottom={["auto", null, "100%"]}
        left={0}
        right={0}
        display="block"
        type="p_xs"
        color="white"
        textDecoration="line-through"
        textDecorationColor="primary"
        whiteSpace="nowrap"
        mb={1}
        sx={tabular}
        data-testid="campaign-price-list"
      >
        {money(list)}
      </Text>
      <Text
        as="span"
        display="block"
        type={isSpec ? "p_lg" : "headline_sm"}
        {...(!isSpec && {
          fontSize: ["20px", null, "24px", null, "32px"],
          lineHeight: ["28px", null, "32px", null, "40px"],
          letterSpacing: 0,
          textTransform: "lowercase",
        })}
        color="white"
        sx={tabular}
        data-testid="campaign-price-final"
      >
        {money(final)}
      </Text>
      <Text
        as="span"
        display="block"
        type="title_xs"
        color="primary"
        mt={isSpec ? [1, null, 0] : "xxs"}
        data-testid="campaign-price-label"
      >
        <Trans id="LBL_CAMPAIGN_SPECIAL_PRICE" />
      </Text>
    </Flex>
  );
};

export default CampaignPriceBlock;
